import React, { useCallback, useContext, useMemo, useRef } from "react";
import { reverse, uniqBy } from "lodash";
import { getInteractiveLayerIds } from "common-client/utils/customMapLayer";
import { CustomMap } from "../../Authorization/types";
import { LayerContext } from "../layers";
import CustomMapLayer, {
  CUSTOM_MAPS_SOURCE_ID_PREFIX,
} from "../layers/customMap";
import {
  EventFeature,
  findRelevantFeatures,
  LayerConfig,
  LayerHook,
  LayerHookBuilder,
  LayerId,
  MapEvent,
} from "./types";
import {
  handleOnClickForCustomMap,
  handleOnHoverForCustomMap,
  useFeatureClickState,
} from "./utils";

export type CustomMapLayerValue = {
  customMapId?: Maybe<string>;
  geometryId?: Maybe<string>;
  fromSavedView?: "true" | "false";
};

export type CustomMapLayerConfig = LayerConfig<CustomMapLayerValue> & {
  maps: Array<CustomMap>;
};

export type GeometryRef = Pick<EventFeature, "id" | "source" | "sourceLayer">;

type SelectFeatureProps = {
  event: MapEvent;
  allInteractiveLayerIds: LayerId[];
  getSortKey?: (features: EventFeature) => string | number;
};

const selectFeature = ({
  event,
  allInteractiveLayerIds,
  getSortKey,
}: SelectFeatureProps) => {
  // there could be multiple layers displaying a feature, so we dedupe by id
  const features = uniqBy(
    findRelevantFeatures({
      event,
      layerId: allInteractiveLayerIds,
      getSortKey,
    }),
    "id",
  );

  return features[0];
};

const useCustomMapHook: LayerHookBuilder<CustomMapLayerConfig> = ({
  config,
  mapLoaded,
  helpers,
}) => {
  const hoveredGeometry = useRef<Maybe<GeometryRef>>(null);
  const { isLayerVisible } = useContext(LayerContext);

  const allInteractiveLayerIds = getInteractiveLayerIds(
    config.maps,
    isLayerVisible,
  ) as LayerId[];

  const clickedGeometry = useFeatureClickState({
    value: {
      customMapId: config.value?.customMapId,
      geometryId: config.value?.geometryId,
    },
    allInteractiveLayerIds,
    helpers,
    mapLoaded,
  });

  const getCustomMapIndex = useCallback(
    (feature: EventFeature) => {
      const customMapId = feature.source.replace(
        CUSTOM_MAPS_SOURCE_ID_PREFIX,
        "",
      );
      return config.maps.findIndex(map => map.id === customMapId);
    },
    [config.maps],
  );

  return useMemo<LayerHook>(() => {
    return {
      interactiveLayerIds: allInteractiveLayerIds,
      zIndex: 2,
      render: () => {
        // we reverse because we want maps with lower
        // displayOrder to be rendered first
        return reverse([...config.maps]).map(map => (
          <CustomMapLayer
            id={map.id}
            tileset={map.tileset}
            key={map.id}
            labelsVisibleOnMap={map.labelsVisibleOnMap}
            isObject={map.isObject}
          />
        ));
      },
      canHandleHover: () => !!config.interactive?.hover,
      onHover: ({ event }) => {
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }

        const feature = selectFeature({
          event,
          allInteractiveLayerIds,
          getSortKey: getCustomMapIndex,
        });

        if (feature) {
          handleOnHoverForCustomMap({
            event,
            helpers,
            hoveredGeometry,
            feature,
          });
        }
      },
      onFocusLost: () => {
        if (hoveredGeometry.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredGeometry.current },
          });
          hoveredGeometry.current = null;
        }
      },
      canHandleClick: event =>
        !!config.interactive?.click && !!event.features?.length,
      onClick: ({ event }) => {
        const feature = selectFeature({
          event,
          allInteractiveLayerIds,
          getSortKey: getCustomMapIndex,
        });

        if (!feature) {
          return false;
        }

        return handleOnClickForCustomMap({
          helpers,
          clickedGeometry,
          feature,
          config,
        });
      },
    };
  }, [
    config,
    allInteractiveLayerIds,
    helpers,
    clickedGeometry,
    getCustomMapIndex,
  ]);
};

export default useCustomMapHook;
